/**
 * WorkHistoryEdit Component
 *
 * Description: This component allows users to view and edit their work history. It includes a form for entering job roles, hospital names, and start/end dates for each position. Users can add or remove work history entries, and submit updates to the server. The component retrieves initial data from the Redux store and updates the store upon successful submission.
 *
 * File Name: WorkHistoryEdit.tsx
 * Date: 02-09-2024
 */

import React, { FormEvent, useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import PageHeader from "src/Components/Customer/PageHeader";
import PreviewProfileButton from "src/Components/Customer/PreviewProfileButton";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import SimpleInput from "src/Components/SimpleInput";
import httpRequest from "src/helpers/httpRequest";
import { IDoctorExperiences } from "src/types/GlobalInterfaces";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { updateDoctorField } from "../../../Store/doctor";
import { setUniqueData } from "src/Store/reducer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faSpinner, faTrash } from "@fortawesome/free-solid-svg-icons";

const emptyObj: IDoctorExperiences = {
  role: "",
  hospitalName: "",
  startDate: "",
  endDate: "",
};

const WorkHistoryEdit: React.FC = () => {
  const { profile } = useSelector((store: RootState) => store.doctor);
  const dispatch = useDispatch();

  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [experiences, setExperiences] = useState<IDoctorExperiences[]>([]);

  /**
   * Handles the form submission to update work history.
   *
   * @param event - The form submission event.
   */
  // API_CALL: Updates the user's "experiences" section with new data
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!payload.role.trim() || !payload.hospitalName.trim()) {
      toast("Role and hospital name are required", { type: "error" });
      return;
    }
    if (payload.role.trim().length > 60 || payload.role.trim().length < 3) {
      toast("Role must be between 3 and 60 characters", { type: "error" });
      return;
    }
    if (payload.hospitalName.trim().length > 60 || payload.hospitalName.trim().length < 3) {
      toast("Hospital name must be between 3 and 60 characters", { type: "error" });
      return;
    }
    if (!payload.startDate.trim() || !payload.endDate.trim()) {
      toast("Start and end date are required", { type: "error" });
      return;
    }
    if (payload.startDate > payload.endDate) {
      toast("Start date must be before end date", { type: "error" });
      return;
    }
    if (payload.startDate > moment().format("YYYY-MM-DD")) {
      toast("Start date cannot be in the future", { type: "error" });
      return;
    }
    setIsLoader(true);
    const newArr =
      editId || editId === 0
        ? experiences.map((v, i) => (i === editId ? payload : v))
        : [...experiences, payload];
    const { res, err } = await httpRequest<{
      message: string;
      data: IDoctorExperiences[];
      percent: number;
      workExperience: string;
    }>({
      method: "post",
      path: "/provider/experience",
      params: { experiences: newArr },
    });
    if (res) {
      const { message, data, percent, workExperience } = res;
      toast(message, { type: "success" }); // api success message
      setCreateMode(false);
      setPayload(emptyObj);
      setEditId(null);
      dispatch(updateDoctorField({ experiences: data }));
      dispatch(
        setUniqueData({ key: "profileCompletion", value: String(percent) })
      );
      dispatch(
        updateDoctorField({ workExperienceMonthly: Number(workExperience) })
      );
    } else {
      toast(err, { type: "error" }); // api error
    }
    setIsLoader(false);
  };

  const [editId, setEditId] = useState<null | number>(null);
  const editSetting = (obj: IDoctorExperiences, id: number) => {
    setEditId(id);
    setPayload(obj);
    setCreateMode(true);
  };
  const cancelEdit = () => {
    setEditId(null);
    setPayload(emptyObj);
    setCreateMode(false);
  };

  const [deleteId, setDeleteId] = useState<null | number>(null);
  const handleDeleteRow = async (id: number) => {
    setDeleteId(id);
    const newArr = experiences.filter((_, i) => i !== id);
    const { res, err } = await httpRequest<{
      data: IDoctorExperiences[];
      percent: number;
      workExperience: string;
    }>({
      method: "post",
      path: "/provider/experience",
      params: { experiences: newArr },
    });
    if (res) {
      const { data, percent , workExperience } = res;
      toast("Work history deleted.", { type: "success" }); // api success message
      setCreateMode(false);
      setPayload(emptyObj);
      setEditId(null);
      dispatch(updateDoctorField({ experiences: data }));
      dispatch(
        setUniqueData({ key: "profileCompletion", value: String(percent) })
      );
      dispatch(
        updateDoctorField({ workExperienceMonthly: Number(workExperience) })
      );
    } else {
      toast(err, { type: "error" }); // api error
    }
    setDeleteId(null);
  };

  const [createMode, setCreateMode] = useState(false);
  const [payload, setPayload] = useState(emptyObj);
  const handleChange = (key: string, val: string) => {
    setPayload((prev) => ({
      ...prev,
      [key]: val,
    }));
  };
  useEffect(() => {
    if (profile) {
      if (!(profile?.experiences || [])?.length) {
        setCreateMode(true);
        setExperiences([]);
      } else {
        // Create a copy of the array before sorting to avoid read-only error
        let experiencesArray = [...(profile?.experiences || [])];
        let sortedExperiences = experiencesArray.sort((a, b) => {
          return new Date(b?.endDate).getTime() - new Date(a?.endDate).getTime();
        });
        
        setExperiences(sortedExperiences);
      }
    }
  }, [profile]);

  return (
    <>
      <PageHeader
        title="Work History"
        isButton={true}
        ButtonComponent={PreviewProfileButton}
      />
      <div className="parent-box minheight-parent-box">
        <p className="font-Poppins-semiBold font-size-18px theme-color-green mb-2">
          Enter your work history and experience.
        </p>
        {/* 17/12/2024 */}
        {experiences?.length
          ? experiences.map((item: IDoctorExperiences, index: number) => (
              <div
                key={index}
                className={`eduction-work-his-change ${
                  editId === index ? "eduction-work-his-change-active" : ""
                }`}
              >
                <div className="absolute flex gap-4 right-4 top-4">
                  <FontAwesomeIcon
                    icon={faPen}
                    className="cursor-pointer font-size-20px text-[#707070]"
                    onClick={() => editSetting(item, index)}
                    id={`edit-work-history-pen-icon-${index + 1}`}
                  />
                  <FontAwesomeIcon
                    icon={deleteId === index ? faSpinner : faTrash}
                    spin={deleteId === index}
                    className="cursor-pointer font-size-20px text-[#707070]"
                    onClick={() => handleDeleteRow(index)}
                    id={`edit-work-history-delete-icon-${index + 1}`}
                  />
                </div>
                <div className="flex gap-20">
                  <div className=" eduction-work-his-change-inner min-w-[20%]">
                    <div>
                      <span className="font-size-18px theme-color-green font-Poppins-Medium block">
                        Job title/role
                      </span>
                      <span className="font-size-15px text-[#2F2F31] break-all font-Poppins-Regular block">
                        {item?.role || ""}
                      </span>
                    </div>
                    <div>
                      <span className="font-size-18px theme-color-green font-Poppins-Medium block">
                        Start Date
                      </span>
                      <span className="font-size-15px text-[#2F2F31] break-all font-Poppins-Regular block">
                        {moment(item?.startDate || "").format("DD-MM-YYYY")}
                      </span>
                    </div>
                  </div>
                  <div className="eduction-work-his-change-inner">
                    <div>
                      <span className="font-size-18px theme-color-green font-Poppins-Medium block">
                        Hospital Name
                      </span>
                      <span className="font-size-15px text-[#2F2F31] break-all font-Poppins-Regular block">
                        {item?.hospitalName || ""}
                      </span>
                    </div>
                    <div>
                      <span className="font-size-18px theme-color-green font-Poppins-Medium block">
                        End Date
                      </span>
                      <span className="font-size-15px text-[#2F2F31] break-all font-Poppins-Regular block">
                        {moment(item?.endDate || "").format("DD-MM-YYYY")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          : null}
        {createMode && (
          <form className="work-history-ui-adjustment" onSubmit={handleSubmit}>
            <SimpleInput
              value={payload?.role || ""}
              onChange={(val) => handleChange("role", val)}
              isLabel={true}
              labelText={"Job title/role"}
              isRequired={true}
              maxLength={60}
              extraStyle={{ borderColor: "#009d91" }}
              id="edit-work-history-job-title"
            />
            <SimpleInput
              value={payload?.hospitalName || ""}
              onChange={(val) => handleChange("hospitalName", val)}
              isLabel={true}
              labelText={"Hospital Name"}
              isRequired={true}
              maxLength={60}
              extraStyle={{ borderColor: "#009d91" }}
              id="edit-work-history-hospital-name"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <SimpleInput
                  value={moment(payload?.startDate).format("YYYY-MM-DD")}
                  onChange={(val) =>
                    handleChange("startDate", `${val}T00:00:00Z`)
                  }
                  isLabel={true}
                  labelText={"Start Date"}
                  isRequired={true}
                  extraStyle={{ borderColor: "#009d91" }}
                  type="date"
                  max={moment().format("YYYY-MM-DD")} // Disable future dates for start date
                  id="edit-work-history-start-date"
                />
              </div>
              <div>
                <SimpleInput
                  value={moment(payload?.endDate).format("YYYY-MM-DD")}
                  onChange={(val) =>
                    handleChange("endDate", `${val}T00:00:00Z`)
                  }
                  isLabel={true}
                  labelText={"End Date"}
                  isRequired={true}
                  extraStyle={{
                    borderColor: "#009d91",
                    cursor: payload?.startDate ? "pointer" : "not-allowed",
                  }}
                  type="date"
                  min={moment(payload?.startDate).format("YYYY-MM-DD")} // Set min date for end date to start date
                  max={moment().format("YYYY-MM-DD")} // Disable future dates for end date
                  disabled={!payload?.startDate} // Disable end date input until start date is selected
                  title={
                    payload?.startDate
                      ? ""
                      : "Please select the start date first."
                  } // if disable then show title
                  id="edit-work-history-end-date"
                />
              </div>
            </div>
            <div className="flex justify-end mt-5 gap-4">
              {(editId || editId === 0) && (
                <PrimaryBtn
                  type="button"
                  ButtonText="Cancel"
                  btnExtraStyle={{
                    borderRadius: "1.5rem",
                    backgroundColor: "#ff0000",
                  }}
                  onPress={cancelEdit}
                  id="edit-work-history-cancel-btn"
                />
              )}
              <PrimaryBtn
                loader={isLoader}
                type="submit"
                ButtonText={"Save"}
                btnExtraStyle={{
                  borderRadius: "1.5rem",
                  backgroundColor: "#004540",
                }}
                id="edit-work-history-save-btn"
              />
            </div>
          </form>
        )}
        {!createMode && (
          <div className="my-5">
            <button
              onClick={() => setCreateMode(true)}
              type="button"
              id="edit-work-history-add-btn"
              className="font-Poppins-semiBold font-size-18px theme-color-green"
            >
              + Add another work history
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default WorkHistoryEdit;
